import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertComponent,
  BreadcrumbComponent,
  Button,
  MUITable,
  PaginationComponent,
  SearchInputComponent,
} from "../../../../common/components";
import {
  getInvoicesListHeaders,
  invoiceBreadcrumbLink,
} from "../order-management-helper";
import { Badge } from "@mui/material";
import { useSetLoading } from "../../../../common/hooks/appearance";
import icons from "../../../../assets/icons/icons";
import { useResourcePermissions } from "../../../../common/hooks/resource-permissions";
import MODULES from "../../../../constants/modules";
import _, { capitalize } from "lodash";
import "./invoices-list.scss";
import CompanyFilter from "../../../user-management/user-management-list/components/company-filter/company-filter";
import { getTenantsList, getTenets } from "../../../../api/tenets";
import { SortOption } from "../../../tenant/tenant-list/tenant-list-helper";
import { routes } from "../../../../constants/routes";
import TransactionsList from "./transactions-list/transactions-list";
import InvoiceFilters from "./invoice-filters/invoice-filters";
import CreateInvoice from "./create-invoice/create-invoice";
import CreateInvoicePopup from "./create-invoice/create-invoice-popup";
import { generateTempInvoiceId, getAllOrders, getInvoices, getOrders } from "../../../../api/order-management";
import { formatDate } from "../../../../utils/utils";

const InvoicesList: React.FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);
  const [searchText, setSearchText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const { canWrite } = useResourcePermissions(MODULES.ORDER_MANAGEMENT);
  const [filters, setFilters] = useState<any>({});
  const [companyFilter, setCompanyFilter] = useState<any>({});
  const [companyNames, setCompanyNames] = useState<any[]>([]);
  const [sortOption, setSortOption] = useState<SortOption>({
    sortBy: null,
    sortDirection: null,
  });
  const [data, setData] = useState<any[]>([]);
  const [crumbleMappingOption, setCrumbMappingOption] = useState<any>(
    invoiceBreadcrumbLink
  );
  const [alert, setAlert] = useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number>(0);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<number | string | null>(
    null
  );
  const [invoiceData, setInvoiceData] = useState<any>({});
  const [selectedOrderTenantId, setSelectedOrderTenantId] = useState<number | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const headers = getInvoicesListHeaders();

  const handleSearch = _.debounce((text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  }, 500);

  const fetchInvoicesList = async () => {
    try {
      setIsLoading(true);
      const params: any = {
        offset: pageSize * (currentPage - 1),
        limit: pageSize,
        searchText,
        sortBy: sortOption.sortBy,
        sortDirection: sortOption.sortDirection,
        ...filters,
      };

      if (companyFilter?.id) {
        params.companyId = companyFilter.id;
      }

      const response = await getInvoices(params);
      setData(response?.data?.data?.invoices);
      setTotalRecords(response?.data?.data?.totalRecords);
    } catch (error) {
      console.error("Failed to fetch invoices list:", error);
      setAlert({
        isOpen: true,
        message: "Failed to fetch invoices. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if(!isOpen && !isViewOpen) {
      fetchInvoicesList();
    }
  }, [currentPage, pageSize, searchText, sortOption, filters, companyFilter, isOpen, isViewOpen]);

  const mapColumnNameToField = (columnName: string): string => {
    const mapping: Record<string, string> = {
      invoiceId: "invoice_number",
      orderId: "order_number",
      companyName: "company_name",
      orderDate: "created_at",
      status: "status",
      totalAmount: "total_amount",
      invoiceDate: "invoice_date",
      dueDate: "due_date",
      transactions: "transaction_count",
    };

    return mapping[columnName] || columnName;
  };

  const handleSort = (column: string, sortType: string) => {
    let sortValues: SortOption = { sortBy: null, sortDirection: null };
    if (sortType !== "Unsort") {
      const mappedField = mapColumnNameToField(column);
      sortValues = { sortBy: mappedField, sortDirection: sortType };
    }
    setCurrentPage(1);
    setSortOption(sortValues);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const getTenentList = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getTenantsList({dropDown: true});
      const items = response?.data?.data?.data?.data || [];
      const uniqueCompanies: { id: number; company_name: string }[] = [];

      for (const item of items) {
        if (item?.company_name) {
          uniqueCompanies.push({
            id: item.id,
            company_name: item.company_name,
          });
        }
      }
      setCompanyNames(uniqueCompanies);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getTenentList();
  }, [getTenentList]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await getAllOrders();
      const rawOrders = response?.data?.data ?? [];

      const filteredOrders = rawOrders.map(({ id, order_number, tenant_id }: { id: number; order_number: string, tenant_id: number }) => ({
        id,
        value: order_number,
        tenantId: tenant_id,
      }));
      setOrderData(filteredOrders);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      setAlert({
        isOpen: true,
        message: "Failed to fetch orders. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const generateInvoiceId = useCallback(async () => {
    if (selectedOrderTenantId === null) return false;
    setIsLoading(true);
    try {
      const response = await generateTempInvoiceId(selectedOrderTenantId);
      setInvoiceData(response?.data?.data);
      setAlert({
        isOpen: true,
        message: "Invoice ID generated successfully.",
        type: "success",
      });
      return true;
    } catch (error) {
      console.error("Failed to generate temporary invoice ID:", error);
      setAlert({
        isOpen: true,
        message: "Failed to generate temporary invoice ID. Please try again.",
        type: "error",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrderTenantId]);

  const handleViewClick = async (invoiceId: number) => {
    setCrumbMappingOption([
      {
        label: "Order Management",
        link: routes.INVOICELIST,
        handleClick: (event: any) => {
          event.preventDefault();
          setCrumbMappingOption(invoiceBreadcrumbLink);
          setIsViewOpen(false);
          setIsOpen(false);
          setCurrentPage(1);
        },
      },
      {
        label: "Invoices",
        link: routes.INVOICELIST,
        handleClick: (event: any) => {
          event.preventDefault();
          setCrumbMappingOption(invoiceBreadcrumbLink);
          setIsViewOpen(false);
          setIsOpen(false);
          setCurrentPage(1);
        },
      },
      { label: "Transactions", link: "" },
    ]);

    setSelectedInvoiceId(invoiceId);
    setIsViewOpen(true);
  };

  const handleAddClick = async () => {
    setCrumbMappingOption([
      {
        label: "Order Management",
        link: routes.INVOICELIST,
        handleClick: (event: any) => {
          event.preventDefault();
          setIsViewOpen(false);
          setIsOpen(false);
          setCurrentPage(1);
        },
      },
      {
        label: "Invoices",
        link: routes.INVOICELIST,
        handleClick: (event: any) => {
          event.preventDefault();
          setCrumbMappingOption(invoiceBreadcrumbLink);
          setIsViewOpen(false);
          setIsOpen(false);
          setCurrentPage(1);
        },
      },
      { label: "Create Invoice", link: "" },
    ]);
  };

  useEffect(() => {
    const triggerInvoiceGeneration = async () => {
      if (selectedOrderTenantId !== null && !isDialogOpen) {
        const success = await generateInvoiceId();
        if (success) {
          setIsOpen(true);
        }
      }
    };

    triggerInvoiceGeneration();
  }, [selectedOrderTenantId, isDialogOpen]);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const formattedData = useMemo(() => {
    return data?.map((item: any) => {
      const {
        id,
        invoice_number,
        status,
        invoice_date,
        amount,
        due_date,
        company_name,
        order_date,
        order_number,
        transaction_count,
      } = item;

      const totalAmount = amount
        ? new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
          }).format(amount)
        : "-";

      return {
        id: id,
        invoiceId: invoice_number,
        orderId: order_number || "-",
        companyName: company_name || "-",
        orderDate: formatDate(order_date) || "-",
        status: capitalize(status) || "-",
        invoiceDate: formatDate(invoice_date) || "-",
        dueDate: formatDate(due_date) || "-",
        totalAmount: totalAmount || "-",
        transactions: transaction_count || 0,
      };
    });
  }, [data]);

  const noResultsTxt = useMemo(() => {
    if (isLoading) {
      return "Loading Invoices...";
    }
    if (searchText) {
      return "No invoices found.";
    }
    return "No invoices available.";
  }, [isLoading, searchText]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const invoiceDateFilterActive =
      filters.invoiceFromDate || filters.invoiceToDate;
    const dueDateFilterActive = filters.dueToDate || filters.dueFromDate;
    const amountFilterActive = filters.minAmount || filters.maxAmount;
    const transactionFilterActive =
      filters.transactionminValue || filters.transactionmaxValue;

    const otherFilters = { ...filters };
    delete otherFilters.invoiceToDate;
    delete otherFilters.invoiceFromDate;
    delete otherFilters.dueFromDate;
    delete otherFilters.dueToDate;
    delete otherFilters.minAmount;
    delete otherFilters.maxAmount;
    delete otherFilters.transactionminValue;
    delete otherFilters.transactionmaxValue;

    const otherActiveFilters = Object.values(otherFilters).filter(
      (val) =>
        val !== undefined &&
        val !== "" &&
        val !== null &&
        !(Array.isArray(val) && val.length === 0)
    ).length;

    if (invoiceDateFilterActive) {
      count += 1;
    }
    if (dueDateFilterActive) {
      count += 1;
    }
    if (amountFilterActive) {
      count += 1;
    }
    if (transactionFilterActive) {
      count += 1;
    }

    count += otherActiveFilters;

    return count;
  }, [filters]);

  return (
    <>
      <div className="order-list-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent breadcrumbLink={crumbleMappingOption} />
        </div>
        {!isOpen && !isViewOpen ? (
          <>
            <div className="order-list-filter-wrapper">
              <div className="search-input-wrapper">
                <SearchInputComponent
                  placeholder="Search by company name, invoice ID etc.,"
                  onSearch={handleSearch}
                />
              </div>
              <div className="filter-right-section d-flex">
                <Badge
                  badgeContent={appliedFilterCount}
                  invisible={appliedFilterCount === 0}
                  color="secondary"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  sx={{
                    "& .MuiBadge-badge": {
                      top: "1px",
                      right: "14px",
                      transform: "scale(1)",
                    },
                  }}
                >
                  <div
                    className={`filter-icon ${isFilterOpen ? "active-filter" : ""}`}
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={icons.FilterIcon} alt="filter-icon" />
                  </div>
                </Badge>
                {canWrite ? (
                  <Button
                    label="Create Invoice"
                    onClick={() => {
                      setIsDialogOpen(true);
                      fetchOrders();
                    }}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div className="order-list-table-filter">
              <div className="order-list-table">
                <MUITable
                  headers={headers}
                  data={formattedData}
                  onSort={handleSort}
                  noResultsTxt={noResultsTxt}
                  onRowClick={(rowData: any) => handleViewClick(rowData.id)}
                />
              </div>
              {isFilterOpen ? (
                <InvoiceFilters
                  open={isFilterOpen}
                  filters={filters}
                  onClose={() => setIsFilterOpen(false)}
                  companyNames={companyNames}
                  onApplyFilters={(filterData, shouldClose = true) => {
                    try {
                      setFilters(filterData);
                      if (shouldClose) setIsFilterOpen(false);
                      setCurrentPage(1);
                    } catch (error: any) {
                      console.error("Failed to apply filters:", error);
                      setAlert({
                        isOpen: true,
                        message: "Failed to apply filters. Please try again.",
                        type: "error",
                      });
                    }
                  }}
                />
              ) : (
                <></>
              )}
            </div>
            {data?.length ? (
              <div className="orders-pagination-container">
                <PaginationComponent
                  totalRecords={totalRecords || 0}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            ) : (
              <></>
            )}
          </>
        ) : isViewOpen ? (
          <TransactionsList invoiceId={selectedInvoiceId} />
        ) : (
          <CreateInvoice setIsOpen={setIsOpen} selectedOrder={selectedOrder} invoiceNumber={invoiceData?.invoiceNumber} dueDate= {invoiceData.dueDate} selectedTenantId={selectedOrderTenantId} />
        )}
        {isDialogOpen && (
          <CreateInvoicePopup
            isDialogOpen={isDialogOpen}
            setIsOpen={setIsDialogOpen}
            handleAddClick={handleAddClick}
            options={orderData}
            setSelectedOrder={setSelectedOrder}
            setSelectedOrderTenantId={setSelectedOrderTenantId}
          />
        )}
        {alert?.isOpen && (
          <AlertComponent
            message={alert?.message || ""}
            type={
              ["info", "success", "error", "warning"].includes(
                alert?.type || ""
              )
                ? (alert?.type as "info" | "success" | "error" | "warning")
                : "info"
            }
            open={alert?.isOpen}
            onClose={() => setAlert({})}
          />
        )}
      </div>
    </>
  );
};
export default InvoicesList;
